import React, { useState, useEffect, useRef, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { GlobalContext } from "../../context/GlobalContext";
import HeroSection     from "./HeroSection";
import AboutUsSection    from "./AboutUsSection";
import ServicesSection   from "./ServicesSection";
import AIProductsSection from "./AIProductsSection";
import FeaturedWorkSection from "./FeaturedWorkSection";
import TrustedBySection    from "./TrustedBySection";
import OurTeamSection    from "./OurTeamSection";
import ContactSection from "./ContactSection";
import "./Home.scss";

const SECTIONS = [
  { id: "about-section",    Component: AboutUsSection    },
  { id: "services-section", Component: ServicesSection   },
  { id: "ai-section",    Component: AIProductsSection },
  { id: "work-section",   Component: FeaturedWorkSection},
  { id: "trusted-section",   Component: TrustedBySection    },
  { id: "team-section",Component: OurTeamSection    },
  { id: "contact-section", Component: ContactSection },
];

const NAV_ITEMS = [
  { id: "hero",             number: "00", label: "Home"         },
  { id: "about-section",    number: "01", label: "About"        },
  { id: "services-section", number: "02", label: "What We Do"   },
  { id: "ai-section",       number: "03", label: "AI Products"  },
  { id: "work-section",     number: "04", label: "Featured Work"},
  { id: "trusted-section",  number: "05", label: "Trusted By"   },
  { id: "team-section",     number: "06", label: "Our Team"     },
  { id: "contact-section",  number: "07", label: "Contact"      },
];

export default function Home({ devUrl }) {
  const [sectionIdx, setSectionIdx] = useState(null); // null = hero
  const [direction,  setDirection]  = useState(1);
  const { setIsHeroActive } = useContext(GlobalContext);

  const go = (nextIdx, dir) => {
    setDirection(dir);
    setSectionIdx(nextIdx);
  };

  const goNext = () => {
    if (sectionIdx === null)                         go(0, 1);
    else if (sectionIdx < SECTIONS.length - 1)      go(sectionIdx + 1, 1);
  };

  const goPrev = () => {
    if (sectionIdx === null) return;
    if (sectionIdx === 0)    go(null, -1);
    else                     go(sectionIdx - 1, -1);
  };

  const goToId = (id) => {
    if (id === "hero") { go(null, -1); return; }
    const idx = SECTIONS.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const dir = sectionIdx === null ? 1 : idx > sectionIdx ? 1 : -1;
    go(idx, dir);
  };

  const activeId = sectionIdx === null ? "hero" : SECTIONS[sectionIdx].id;

  // Sync logo size with hero state
  useEffect(() => {
    setIsHeroActive(sectionIdx === null);
  }, [sectionIdx]); // eslint-disable-line

  // Reset logo to full size when navigating away from home
  useEffect(() => {
    return () => setIsHeroActive(true);
  }, []); // eslint-disable-line

  return (
    <div className="page-root">

      {/* Global side nav — always visible on desktop, hidden <992px via CSS */}
      <aside className="scrolly-side-nav">
        <ul>
          {NAV_ITEMS.map((item) => (
            <li key={item.id} className={activeId === item.id ? "active" : ""}>
              <button onClick={() => goToId(item.id)}>
                <span className="number">{item.number}</span>
                <span className="label">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Mobile section label — visible <992px via CSS */}
      <div className="scrolly-mobile-label">
        <span className="scrolly-mobile-label__name">
          {NAV_ITEMS.find(n => n.id === activeId)?.label}
        </span>
      </div>

      {/* Progress dots — always rendered so hero dot is visible on mobile */}
      <div className="scrolly-progress">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            className={`scrolly-progress__dot ${activeId === item.id ? "active" : ""}`}
            onClick={() => goToId(item.id)}
            aria-label={`Go to ${item.label}`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait" custom={direction}>
        {sectionIdx === null ? (
          <HeroSection
            key="hero"
            url={devUrl}
            direction={direction}
            onExhausted={goNext}
          />
        ) : (
          <ScrollyStage
            key="scrolly"
            direction={direction}
            sectionIdx={sectionIdx}
            sections={SECTIONS}
            onNext={goNext}
            onPrev={goPrev}
            onGoToId={goToId}
            devUrl={devUrl}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ScrollyStage
// ONE wheel listener lives here. It asks the active section via
// sectionIntentRef.current(dir) whether it wants to consume the tick.
// Sections return true (consumed) or false (pass to layout).
// Sections register their handler by calling registerIntent(fn) on mount.
// ─────────────────────────────────────────────────────────────────────────────

const slideVariants = {
  enter:  (dir) => ({ y: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { y: 0, opacity: 1, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
  exit:   (dir) => ({ y: dir > 0 ? -60 : 60, opacity: 0,
                      transition: { duration: 0.45, ease: [0.76, 0, 0.24, 1] } }),
};

function ScrollyStage({ direction, sectionIdx, sections, onNext, onPrev, onGoToId, devUrl }) {
  const { id, Component } = sections[sectionIdx];

  // The active section registers its intent handler here
  // fn(dir) → true (consumed internally) | false (pass to layout)
  const sectionIntentRef = useRef(() => false);

  // Single debounce for the whole stage — one scroll = one action
  const debounceRef = useRef(false);

  // Entry cooldown — ignore wheel events for 800ms after ScrollyStage mounts.
  // This swallows the wheel event that came from HeroSection's onExhausted call.
  const entryLockRef = useRef(true);
  useEffect(() => {
    const t = setTimeout(() => { entryLockRef.current = false; }, 800);
    return () => clearTimeout(t);
  }, []);

  const handleWheel = (dir) => {
    if (entryLockRef.current) return;
    if (debounceRef.current)  return;

    // Ask active section first
    const consumed = sectionIntentRef.current(dir);
    if (consumed) {
      // Section handled it — short debounce so rapid scrolling doesn't skip steps
      debounceRef.current = true;
      setTimeout(() => { debounceRef.current = false; }, 700);
      return;
    }

    // Section passed — move to next/prev section
    debounceRef.current = true;
    setTimeout(() => { debounceRef.current = false; }, 900);
    if (dir > 0) onNext();
    else         onPrev();
  };

  useEffect(() => {
    const onWheel = (e) => {
      // Let the chatbot scroll its own panel natively instead of
      // hijacking the wheel event for section scroll-jacking.
      if (e.target.closest && e.target.closest(".exd-messages, .exd-chat-input")) return;
      e.preventDefault();
      handleWheel(e.deltaY > 0 ? 1 : -1);
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [onNext, onPrev, sectionIdx]); // eslint-disable-line

  const touchY = useRef(0);
  useEffect(() => {
    const onStart = (e) => { touchY.current = e.touches[0].clientY; };
    const onEnd   = (e) => {
      const diff = touchY.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) < 40) return;
      handleWheel(diff > 0 ? 1 : -1);
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchend",   onEnd,   { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchend",   onEnd);
    };
  }, [onNext, onPrev, sectionIdx]); // eslint-disable-line

  return (
    <motion.div
      className="scrolly-root"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.4 } }}
    >
      <div className="scrolly-stage">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={id}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="scrolly-panel"
          >
            {/*
              Pass registerIntent so the section can tell ScrollyStage
              how to handle scroll ticks. No wheel listener in sections.
            */}
            <Component
              sectionId={id}
              registerIntent={(fn) => { sectionIntentRef.current = fn; }}
              url={devUrl}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}